import styled, { css } from "styled-components";

import {
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { IThemeProps } from "@/styles/Theme";

type ResultProps = IThemeProps<{
  $type: OptionTypeEnum;
  $mode: OptionModeEnum;
  $answer?: OptionEnum;
  $percent?: number;
  $progressBar?: boolean;
}>;
const Result = styled.div<ResultProps>`
  height: 13px;
  ${(props: ResultProps) => props.$type === OptionTypeEnum.poll && PollResult}
  ${(props: ResultProps) => props.$type === OptionTypeEnum.quiz && QuizResult}
`;

const WhiteBox = styled.div`
  background-color: ${(props: IThemeProps) => props.theme.colors.white};
  padding: ${(props: IThemeProps) => props.theme.borders.answerTile};
`;

type FlexboxProps = IThemeProps<{
  $type: OptionTypeEnum;
  $center?: boolean;
  $mode?: OptionModeEnum;
  $answer?: OptionEnum;
}>;
const Flexbox = styled.div<FlexboxProps>`
  height: 13px;
  display: flex;
  align-items: center;
  position: relative;
  border: ${(props: FlexboxProps) =>
    `${props.theme.borders.resultBarBorder} solid ${props.theme.colors.primary}`};
  ${(props: FlexboxProps) =>
    props.$type === OptionTypeEnum.poll && PollFlexbox};
  ${(props: FlexboxProps) =>
    props.$type === OptionTypeEnum.quiz && QuizFlexbox};
`;

const PollFlexbox = css`
  justify-content: ${(props: FlexboxProps) =>
    props.$center ? "center" : "flex-start"};
  background-color: ${(props: FlexboxProps) => {
    const {
      pollOptionResultBarBackground,
      pollOptionResultBarBackgroundModeResult,
      pollOptionResultBarBackgroundModeResultVote,
    } = props.theme.colors;

    if (props.$mode === OptionModeEnum.results) {
      if (props.$answer === OptionEnum.vote) {
        return pollOptionResultBarBackgroundModeResultVote;
      }
      return pollOptionResultBarBackgroundModeResult;
    }
    return pollOptionResultBarBackground;
  }};
`;

const QuizFlexbox = css`
  justify-content: center;
  background-color: ${(props: FlexboxProps) => {
    const {
      quizOptionResultBarBackground,
      quizOptionResultBarBackgroundCorrect,
      quizOptionResultBarBackgroundWrong,
    } = props.theme.colors;

    if (props.$answer) {
      if (props.$answer === OptionEnum.correct) {
        return quizOptionResultBarBackgroundCorrect;
      } else {
        return quizOptionResultBarBackgroundWrong;
      }
    }
    return quizOptionResultBarBackground;
  }};
`;

const PollResult = css`
  width: ${({ $mode, $percent, $progressBar }: ResultProps) => {
    if ($mode === OptionModeEnum.results) {
      return $progressBar ? $percent : 100;
    }
    return 0;
  }}%;
  transition: ${({ $progressBar }: ResultProps) =>
    $progressBar ? "width 800ms ease-in" : "none"};
  background-color: ${({
    $answer,
    $mode,
    $progressBar,
    theme: {
      colors: {
        pollOptionResultBarBackground,
        pollOptionResultBarModeResult,
        pollOptionResultBarModeResultVote,
      },
    },
  }: ResultProps) => {
    if ($mode === OptionModeEnum.results && $answer === OptionEnum.vote) {
      return pollOptionResultBarModeResultVote;
    }

    return $progressBar
      ? pollOptionResultBarModeResult
      : pollOptionResultBarBackground;
  }};
`;

const QuizResult = css`
  width: ${(props: ResultProps) =>
    props.$answer &&
    (props.$answer === OptionEnum.correct || props.$answer === OptionEnum.wrong)
      ? 100
      : 0}%;
  transition: width 0.8s ease-in;
  background-color: ${(props: ResultProps) => {
    const { success, error } = props.theme.colors;
    return props.$answer === OptionEnum.correct ? success : error;
  }};
`;

type TextProps = IThemeProps<{
  $type: OptionTypeEnum;
  $answer?: OptionEnum;
  $alignRight: boolean;
  $isMultipleChoice?: boolean;
}>;
const Text = styled.span<TextProps>`
  position: absolute;
  ${(props: TextProps) => props.$alignRight && "right: 6px;"}
  font-size: ${(props: TextProps) => props.theme.typography.h100};
  color: ${(props: TextProps) =>
    props.$isMultipleChoice
      ? props.theme.colors.white
      : props.theme.colors.resultBarText};
  font-weight: 600;
`;

export { Flexbox, Result, Text, WhiteBox };
